import { AlertTriangle, AlignLeft, AlignRight, LayoutList, Plus, RefreshCw, Trash2 } from 'lucide-react';
import React from 'react';

export interface TocEditorData {
  items: string[];
  align?: 'top' | 'left' | 'right';
}

interface Props {
  data: TocEditorData;
  onChange: (data: Record<string, unknown>) => void;
  /** Headings from all sibling sections — used for auto-sync */
  sectionHeadings?: string[];
}

const ALIGN_OPTIONS: { value: TocEditorData['align']; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { value: 'top',   label: 'Top (inline)',     Icon: LayoutList   },
  { value: 'left',  label: 'Left sidebar',     Icon: AlignLeft    },
  { value: 'right', label: 'Right sidebar',    Icon: AlignRight   },
];

export const TocSectionEditor: React.FC<Props> = ({ data, onChange, sectionHeadings = [] }) => {
  const items: string[] = data.items ?? [];
  const align = data.align ?? 'top';

  // Detect mismatch: TOC items don't exactly match the section headings.
  const validHeadings = sectionHeadings.filter(Boolean);
  const hasMismatch =
    validHeadings.length > 0 &&
    (items.length !== validHeadings.length ||
      items.some((item, i) => item.trim() !== validHeadings[i]?.trim()));

  const set = (patch: Partial<TocEditorData>) =>
    onChange({ ...data, ...patch });

  const updateItem = (idx: number, val: string) =>
    set({ items: items.map((it, i) => (i === idx ? val : it)) });

  const addItem = () => set({ items: [...items, ''] });

  const removeItem = (idx: number) =>
    set({ items: items.filter((_, i) => i !== idx) });

  const syncFromHeadings = () => {
    const fresh = sectionHeadings.filter(Boolean);
    if (fresh.length) set({ items: fresh });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Alignment picker */}
      <div>
        <p className="t-label mb-2">Position</p>
        <div className="flex gap-2">
          {ALIGN_OPTIONS.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => set({ align: value })}
              title={label}
              className={`ds-chip ds-chip-button ${
                align === value
                  ? 'is-active'
                  : ''
              }`}
              aria-selected={align === value ? 'true' : undefined}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Mismatch warning */}
      {hasMismatch && (
        <div className="ds-recessed-warn flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <div className="flex-1 min-w-0">
            <p className="t-tag leading-snug">
              TOC items don't match section headings.
            </p>
          </div>
          <button
            type="button"
            onClick={syncFromHeadings}
            className="ds-btn ds-btn-ghost ds-btn-sm whitespace-nowrap"
          >
            Fix now →
          </button>
        </div>
      )}

      {/* Items list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="t-label">Items</p>
          {sectionHeadings.length > 0 && (
            <button
              type="button"
              onClick={syncFromHeadings}
              title="Replace items with the headings from other sections"
              className="ds-btn ds-btn-ghost ds-btn-sm"
            >
              <RefreshCw className="w-3 h-3" />
              Sync from section headings
            </button>
          )}
        </div>

        {items.length === 0 && (
          <p className="t-meta italic mb-2">
            No items yet. Click "Sync from section headings" or add manually.
          </p>
        )}

        <div className="flex flex-col gap-2">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="t-meta t-num w-5 text-right shrink-0">
                {idx + 1}.
              </span>
              <input
                value={item}
                onChange={(e) => updateItem(idx, e.target.value)}
                placeholder={`TOC item ${idx + 1}`}
                className="ds-input ds-input-sm flex-1"
              />
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="ds-btn ds-btn-ghost ds-btn-sm p-0 w-7 hover:text-[var(--ds-danger)]"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addItem}
          className="mt-2 ds-btn ds-btn-secondary ds-btn-sm w-fit"
        >
          <Plus className="w-4 h-4" />
          Add item
        </button>
      </div>
    </div>
  );
};
